import React from 'react';
import { AbsoluteFill } from 'remotion';
import { LayerContainer } from './types';
import { OverlayTransitionItem } from '../types/overlay-transition';
import { wrapInSequence } from '../types/base';
import { ThemeSystemConfig } from '../../clipCreator/remotion/Components/Theme/Theme';
import { AspectRatio } from '../AspectRatio';
import { TimelineDefaults } from '../TimelineDefaults';

/**
 * Layer container for overlay transitions
 * Manages video overlays with alpha channel that play between scenes
 * Positioned above scene content but below captions and branding
 */
export class OverlayTransitionLayerContainer implements LayerContainer {
    items: OverlayTransitionItem[] = [];

    /**
     * Add an overlay transition item to this layer
     */
    addOverlayTransition(item: OverlayTransitionItem): void {
        this.items.push(item);
    }

    /**
     * Remove an overlay transition item by ID
     */
    removeOverlayTransition(id: string): void {
        this.items = this.items.filter(item => item.id !== id);
    }

    /**
     * Find overlay transitions associated with a specific scene
     */
    getTransitionsForScene(sceneId: string): OverlayTransitionItem[] {
        return this.items.filter(item => item.associatedSceneId === sceneId);
    }

    /**
     * Clear all overlay transition items
     */
    clear(): void {
        this.items = [];
    }

    /**
     * Render all overlay transition items in this layer
     * Each item is wrapped in its own sequence for proper timing
     */
    render(fps: number, theme: ThemeSystemConfig, aspectRatio: AspectRatio, defaults: TimelineDefaults, videoConfig?: { width: number; height: number }): React.ReactNode {
        if (this.items.length === 0) {
            return null;
        }

        // Video configuration should be passed from Timeline
        if (!videoConfig) {
            throw new Error('OverlayTransitionLayerContainer.render requires videoConfig parameter');
        }

        if (process.env.NODE_ENV === 'development') {
            console.log(`[OVERLAY TRANSITION LAYER] Rendering ${this.items.length} overlay transitions`);
        }

        return (
            <AbsoluteFill>
                {this.items.map((item) => {
                    if (process.env.NODE_ENV === 'development') {
                        console.log(`[OVERLAY TRANSITION] Rendering ${item.id}:`, {
                            startAt: item.startAt,
                            duration: item.overlayDuration,
                            videoSrc: item.videoSrc,
                            hasAudio: !!item.audioEffect,
                            associatedScene: item.associatedSceneId
                        });
                    }

                    // Wrap each overlay transition in a sequence for proper timing
                    return wrapInSequence(
                        item,
                        fps,
                        item.render(fps, theme, aspectRatio, defaults, videoConfig),
                        'OverlayTransition',
                        item.audioEffect ? 'with-audio' : 'video-only'
                    );
                })}
            </AbsoluteFill>
        );
    }

    /**
     * Calculate the total duration of all overlay transitions
     * Useful for timeline calculations
     */
    getTotalDuration(): number {
        if (this.items.length === 0) return 0;
        
        return Math.max(...this.items.map(item => item.startAt + item.overlayDuration));
    }

    /**
     * Get overlay transitions that are active at a specific time
     * Useful for debugging and timeline visualization
     */
    getActiveTransitionsAtTime(time: number): OverlayTransitionItem[] {
        return this.items.filter(item => {
            const startTime = item.startAt;
            const endTime = item.startAt + item.overlayDuration;
            return time >= startTime && time <= endTime;
        });
    }

    /**
     * Validate all overlay transitions for common issues
     * Returns array of validation warnings/errors
     */
    validate(): string[] {
        const warnings: string[] = [];

        this.items.forEach(item => {
            // Check for reasonable duration
            if (item.overlayDuration <= 0) {
                warnings.push(`Overlay transition ${item.id} has invalid duration: ${item.overlayDuration}`);
            }

            if (item.overlayDuration > 10) {
                warnings.push(`Overlay transition ${item.id} has very long duration: ${item.overlayDuration}s (consider shorter transitions)`);
            }

            // Check for valid video source
            if (!item.videoSrc || item.videoSrc.trim() === '') {
                warnings.push(`Overlay transition ${item.id} has empty video source`);
            }

            // Check audio configuration if present
            if (item.audioEffect) {
                if (!item.audioEffect.sound || item.audioEffect.sound.trim() === '') {
                    warnings.push(`Overlay transition ${item.id} has empty audio source`);
                }

                const timing = item.audioEffect.timing;
                if (timing?.volume !== undefined && (timing.volume < 0 || timing.volume > 1)) {
                    warnings.push(`Overlay transition ${item.id} has invalid audio volume: ${timing.volume} (should be 0-1)`);
                }

                if (timing?.delay !== undefined && timing.delay < 0) {
                    warnings.push(`Overlay transition ${item.id} has negative audio delay: ${timing.delay}`);
                }
            }
        });

        return warnings;
    }
}